import type { FightingStyle } from '@character-creator/types';

/**
 * SRD 5.1 Fighting Styles
 * Available to Fighter (level 1), Paladin (level 2), and Ranger (level 2).
 */
export const fightingStyles: FightingStyle[] = [
  {
    id: 'archery',
    name: 'Archery',
    description: 'You gain a +2 bonus to attack rolls you make with ranged weapons.',
    availableTo: ['fighter', 'ranger'],
    source: 'srd-5.1',
  },
  {
    id: 'defense',
    name: 'Defense',
    description: 'While you are wearing armor, you gain a +1 bonus to AC.',
    availableTo: ['fighter', 'paladin', 'ranger'],
    source: 'srd-5.1',
  },
  {
    id: 'dueling',
    name: 'Dueling',
    description:
      'When you are wielding a melee weapon in one hand and no other weapons, you gain a +2 bonus to damage rolls with that weapon.',
    availableTo: ['fighter', 'paladin', 'ranger'],
    source: 'srd-5.1',
  },
  {
    id: 'great-weapon-fighting',
    name: 'Great Weapon Fighting',
    description:
      'When you roll a 1 or 2 on a damage die for an attack you make with a melee weapon that you are wielding with two hands, you can reroll the die and must use the new roll, even if the new roll is a 1 or a 2. The weapon must have the two-handed or versatile property for you to gain this benefit.',
    availableTo: ['fighter', 'paladin'],
    source: 'srd-5.1',
  },
  {
    id: 'protection',
    name: 'Protection',
    description:
      "When a creature you can see attacks a target other than you that is within 5 feet of you, you can use your reaction to impose disadvantage on the attack roll. You must be wielding a shield.",
    availableTo: ['fighter', 'paladin'],
    source: 'srd-5.1',
  },
  {
    id: 'two-weapon-fighting',
    name: 'Two-Weapon Fighting',
    description:
      'When you engage in two-weapon fighting, you can add your ability modifier to the damage of the second attack.',
    availableTo: ['fighter', 'ranger'],
    source: 'srd-5.1',
  },
];

// Lookup map for efficient access
export const fightingStyleMap = new Map<string, FightingStyle>(
  fightingStyles.map((fs) => [fs.id, fs])
);

// Helper function
export function getFightingStyle(id: string): FightingStyle | undefined {
  return fightingStyleMap.get(id);
}

/**
 * Get all fighting styles, optionally filtered by source
 */
export function getFightingStyles(source?: string): FightingStyle[] {
  return source ? fightingStyles.filter((fs) => fs.source === source) : fightingStyles;
}

/**
 * Get fighting styles available to a specific class
 */
export function getFightingStylesForClass(
  classId: 'fighter' | 'paladin' | 'ranger',
  source?: string
): FightingStyle[] {
  const filtered = fightingStyles.filter((fs) => fs.availableTo.includes(classId));
  return source ? filtered.filter((fs) => fs.source === source) : filtered;
}

/**
 * Get the level at which a class gains Fighting Style feature
 */
export function getFightingStyleLevel(classId: string): number | null {
  switch (classId) {
    case 'fighter':
      return 1;
    case 'paladin':
      return 2;
    case 'ranger':
      return 2;
    default:
      return null;
  }
}
